/**
 * The HEIF container of the macOS dynamic wallpaper (2026-10-12): macOS wants every picture of the
 * day in ONE .heic, with the schedule as an XMP item that describes the primary picture. Each frame
 * comes coded from the encoder (hevc-bitstream.ts turns it into an item), and all of them are
 * written as the items of a single file:
 *
 *   ftyp  heic / mif1
 *   meta  hdlr pict · dinf · pitm · iinf (N × hvc1 + 1 × mime) · iref cdsc · iprp (ipco + ipma) · iloc
 *   mdat  the N coded pictures, then the XMP packet
 *
 * readPrimaryPicture reads a plain picture back (the round trip the tests make): construction
 * method 0 (file offsets), no grids; auxiliary items such as an alpha plane are left behind.
 */

/** one coded picture, ready to be written as an item */
export interface HevcPicture {
  width: number;
  height: number;
  /** the property boxes (hvcC first), each a complete box */
  properties: Uint8Array[];
  /** the coded picture: length-prefixed NAL units */
  data: Uint8Array;
}

interface Box { type: string; start: number; body: number; end: number; }

function u16( d: Uint8Array, o: number ): number { return ( d[ o ] << 8 ) | d[ o + 1 ]; }
function u32( d: Uint8Array, o: number ): number { return ( ( d[ o ] << 24 ) >>> 0 ) + ( ( d[ o + 1 ] << 16 ) | ( d[ o + 2 ] << 8 ) | d[ o + 3 ] ); }
function uN( d: Uint8Array, o: number, size: number ): number {
  switch ( size ) {
    case 0: return 0;
    case 2: return u16( d, o );
    case 4: return u32( d, o );
    case 8: return u32( d, o ) * 4294967296 + u32( d, o + 4 );
  }
  throw new Error( `HEIF: unsupported field size ${ size }` );
}

function boxes( d: Uint8Array, start: number, end: number ): Box[] {
  const out: Box[] = [];
  let o = start;
  while ( o + 8 <= end ) {
    let size = u32( d, o );
    const type = String.fromCharCode( d[ o + 4 ], d[ o + 5 ], d[ o + 6 ], d[ o + 7 ] );
    let body = o + 8;
    if ( size === 1 ) {
      size = uN( d, o + 8, 8 );
      body = o + 16;
    } else if ( size === 0 ) {
      size = end - o;
    }
    if ( size < 8 || o + size > end ) {
      throw new Error( `HEIF: broken ${ type } box` );
    }
    out.push( { type, start: o, body, end: o + size } );
    o += size;
  }
  return out;
}

function child( d: Uint8Array, parent: Box, type: string, skip = 0 ): Box | undefined {
  return boxes( d, parent.body + skip, parent.end ).find( b => b.type === type );
}

/** the primary picture of a HEIC holding plain HEVC pictures */
export function readPrimaryPicture( file: Uint8Array ): HevcPicture {
  const top = boxes( file, 0, file.length );
  const meta = top.find( b => b.type === 'meta' );
  if ( !meta ) throw new Error( 'HEIF: no meta box' );
  // meta is a full box: version + flags before its children
  const pitm = child( file, meta, 'pitm', 4 );
  const iloc = child( file, meta, 'iloc', 4 );
  const iprp = child( file, meta, 'iprp', 4 );
  if ( !pitm || !iloc || !iprp ) throw new Error( 'HEIF: incomplete meta box' );
  const primary = file[ pitm.body ] === 0 ? u16( file, pitm.body + 4 ) : u32( file, pitm.body + 4 );

  // ---- the primary item's extents (iloc)
  const v = file[ iloc.body ];
  let o = iloc.body + 4;
  const offsetSize = file[ o ] >> 4, lengthSize = file[ o ] & 15;
  const baseSize = file[ o + 1 ] >> 4, indexSize = v === 1 || v === 2 ? file[ o + 1 ] & 15 : 0;
  o += 2;
  const count = v < 2 ? u16( file, o ) : u32( file, o );
  o += v < 2 ? 2 : 4;
  let parts: Uint8Array[] | null = null;
  for ( let i = 0; i < count; i++ ) {
    const id = v < 2 ? u16( file, o ) : u32( file, o );
    o += v < 2 ? 2 : 4;
    let method = 0;
    if ( v === 1 || v === 2 ) {
      method = u16( file, o ) & 15;
      o += 2;
    }
    o += 2; // data_reference_index
    const base = uN( file, o, baseSize );
    o += baseSize;
    const extents = u16( file, o );
    o += 2;
    const mine: Uint8Array[] = [];
    for ( let e = 0; e < extents; e++ ) {
      o += indexSize;
      const off = uN( file, o, offsetSize );
      o += offsetSize;
      const len = uN( file, o, lengthSize );
      o += lengthSize;
      mine.push( file.subarray( base + off, base + off + len ) );
    }
    if ( id === primary ) {
      if ( method !== 0 ) throw new Error( 'HEIF: unsupported construction method' );
      parts = mine;
    }
  }
  if ( !parts ) throw new Error( 'HEIF: the primary item has no data' );

  // ---- the primary item's properties (ipco + ipma)
  const ipco = child( file, iprp, 'ipco' );
  const ipma = child( file, iprp, 'ipma' );
  if ( !ipco || !ipma ) throw new Error( 'HEIF: no item properties' );
  const props = boxes( file, ipco.body, ipco.end );
  const mv = file[ ipma.body ], wide = ( file[ ipma.body + 3 ] & 1 ) === 1;
  o = ipma.body + 4;
  const entries = u32( file, o );
  o += 4;
  const properties: Uint8Array[] = [];
  let width = 0, height = 0;
  for ( let i = 0; i < entries; i++ ) {
    const id = mv < 1 ? u16( file, o ) : u32( file, o );
    o += mv < 1 ? 2 : 4;
    const n = file[ o++ ];
    for ( let a = 0; a < n; a++ ) {
      const index = wide ? u16( file, o ) & 0x7fff : file[ o ] & 0x7f;
      o += wide ? 2 : 1;
      const box = props[ index - 1 ];
      if ( id !== primary || !box ) continue;
      // what a plain picture needs; a colr of its own describes its conversion
      if ( [ 'hvcC', 'ispe', 'pixi', 'colr' ].indexOf( box.type ) < 0 ) continue;
      if ( box.type === 'ispe' ) {
        width = u32( file, box.body + 4 );
        height = u32( file, box.body + 8 );
      }
      properties.push( file.slice( box.start, box.end ) );
    }
  }
  const type = ( b: Uint8Array ) => String.fromCharCode( b[ 4 ], b[ 5 ], b[ 6 ], b[ 7 ] );
  properties.sort( ( a, b ) => ( type( a ) === 'hvcC' ? -1 : 0 ) - ( type( b ) === 'hvcC' ? -1 : 0 ) );
  if ( !properties.length || type( properties[ 0 ] ) !== 'hvcC' || !width ) {
    throw new Error( 'HEIF: the primary item is not a plain HEVC picture' );
  }

  const length = parts.reduce( ( s, p ) => s + p.length, 0 );
  const data = new Uint8Array( length );
  let at = 0;
  for ( const p of parts ) {
    data.set( p, at );
    at += p.length;
  }
  return { width, height, properties, data };
}

// ---------------------------------------------------------------- writing

class Writer {
  private chunks: number[] = [];
  get length(): number { return this.chunks.length; }
  u8( v: number ): this { this.chunks.push( v & 255 ); return this; }
  u16( v: number ): this { return this.u8( v >> 8 ).u8( v ); }
  u32( v: number ): this { return this.u8( v >>> 24 ).u8( v >>> 16 ).u8( v >>> 8 ).u8( v ); }
  str( s: string ): this { for ( let i = 0; i < s.length; i++ ) this.u8( s.charCodeAt( i ) ); return this; }
  bytes( b: Uint8Array | number[] ): this { for ( let i = 0; i < b.length; i++ ) this.chunks.push( b[ i ] ); return this; }
  done(): Uint8Array { return Uint8Array.from( this.chunks ); }
}

function box( type: string, body: Uint8Array | number[] ): Uint8Array {
  return new Writer().u32( body.length + 8 ).str( type ).bytes( body ).done();
}

function fullBox( type: string, version: number, flags: number, body: Uint8Array | number[] ): Uint8Array {
  const w = new Writer().u8( version ).u8( flags >> 16 ).u8( flags >> 8 ).u8( flags ).bytes( body );
  return box( type, w.done() );
}

function concat( parts: Uint8Array[] ): Uint8Array {
  const out = new Uint8Array( parts.reduce( ( s, p ) => s + p.length, 0 ) );
  let at = 0;
  for ( const p of parts ) {
    out.set( p, at );
    at += p.length;
  }
  return out;
}

function sameBytes( a: Uint8Array, b: Uint8Array ): boolean {
  if ( a.length !== b.length ) return false;
  for ( let i = 0; i < a.length; i++ ) if ( a[ i ] !== b[ i ] ) return false;
  return true;
}

/**
 * sRGB primaries and transfer, BT.601 full range: how the page turns RGB into YCbCr (rgbaToI420),
 * said out loud so every reader converts it back the same way
 */
const SRGB_NCLX = box( 'colr', new Writer().str( 'nclx' ).u16( 1 ).u16( 13 ).u16( 6 ).u8( 0x80 ).done() );

/**
 * Every picture as an item of one HEIC, `primary` the one shown where a still picture is wanted
 * (Finder, the wallpaper list) and the one the XMP describes.
 */
export function writeDynamicHeic( pictures: HevcPicture[], primary: number, xmp: string ): Uint8Array {
  if ( !pictures.length ) throw new Error( 'HEIF: no pictures' );
  const xmpBytes = new TextEncoder().encode( xmp );
  const n = pictures.length;
  const xmpId = n + 1;
  const primaryId = primary + 1;

  // ---- the properties, each distinct box once (frames of one size share ispe, pixi and colr)
  const ipco: Uint8Array[] = [];
  const indexOf = ( b: Uint8Array ): number => {
    const found = ipco.findIndex( p => sameBytes( p, b ) );
    if ( found > -1 ) return found + 1;
    ipco.push( b );
    return ipco.length;
  };
  const associations = pictures.map( pic => {
    const list = pic.properties.map( p => ( { index: indexOf( p ), essential: String.fromCharCode( p[ 4 ], p[ 5 ], p[ 6 ], p[ 7 ] ) === 'hvcC' } ) );
    if ( !pic.properties.some( p => String.fromCharCode( p[ 4 ], p[ 5 ], p[ 6 ], p[ 7 ] ) === 'colr' ) ) {
      list.push( { index: indexOf( SRGB_NCLX ), essential: false } );
    }
    return list;
  } );
  const wide = ipco.length > 127;
  const ipma = new Writer().u32( n );
  associations.forEach( ( list, i ) => {
    ipma.u16( i + 1 ).u8( list.length );
    for ( const a of list ) {
      if ( wide ) ipma.u16( ( a.essential ? 0x8000 : 0 ) | a.index );
      else ipma.u8( ( a.essential ? 0x80 : 0 ) | a.index );
    }
  } );
  const iprp = box( 'iprp', concat( [ box( 'ipco', concat( ipco ) ), fullBox( 'ipma', 0, wide ? 1 : 0, ipma.done() ) ] ) );

  // ---- the items
  const infes: Uint8Array[] = pictures.map( ( _p, i ) =>
    fullBox( 'infe', 2, 0, new Writer().u16( i + 1 ).u16( 0 ).str( 'hvc1' ).u8( 0 ).done() ) );
  infes.push( fullBox( 'infe', 2, 0, new Writer().u16( xmpId ).u16( 0 ).str( 'mime' ).u8( 0 ).str( 'application/rdf+xml' ).u8( 0 ).done() ) );
  const iinf = fullBox( 'iinf', 0, 0, concat( [ new Writer().u16( infes.length ).done(), ...infes ] ) );
  const hdlr = fullBox( 'hdlr', 0, 0, new Writer().u32( 0 ).str( 'pict' ).u32( 0 ).u32( 0 ).u32( 0 ).u8( 0 ).done() );
  // the data lives in this file (a self-contained url entry)
  const dinf = box( 'dinf', fullBox( 'dref', 0, 0, concat( [ new Writer().u32( 1 ).done(), fullBox( 'url ', 0, 1, [] ) ] ) ) );
  const pitm = fullBox( 'pitm', 0, 0, new Writer().u16( primaryId ).done() );
  // the XMP describes the primary picture
  const iref = fullBox( 'iref', 0, 0, box( 'cdsc', new Writer().u16( xmpId ).u16( 1 ).u16( primaryId ).done() ) );

  // ---- iloc: one extent per item, absolute offsets into the mdat that follows meta
  const payloads = [ ...pictures.map( p => p.data ), xmpBytes ];
  const iloc = ( mdatData: number ): Uint8Array => {
    const w = new Writer().u8( 0x44 ).u8( 0x00 ).u16( payloads.length );
    let at = mdatData;
    payloads.forEach( ( p, i ) => {
      w.u16( i + 1 ).u16( 0 ).u16( 1 ).u32( at ).u32( p.length );
      at += p.length;
    } );
    return fullBox( 'iloc', 0, 0, w.done() );
  };

  const ftyp = box( 'ftyp', new Writer().str( 'heic' ).u32( 0 ).str( 'mif1' ).str( 'heic' ).done() );
  const metaOf = ( mdatData: number ) => fullBox( 'meta', 0, 0, concat( [ hdlr, dinf, pitm, iloc( mdatData ), iinf, iref, iprp ] ) );
  // the iloc's size does not depend on the offsets it holds, so one dry run gives the layout
  const metaSize = metaOf( 0 ).length;
  const mdatSize = 8 + payloads.reduce( ( s, p ) => s + p.length, 0 );
  if ( ftyp.length + metaSize + mdatSize > 0xffffffff ) throw new Error( 'HEIF: the file would pass 4 GB' );
  const meta = metaOf( ftyp.length + metaSize + 8 );
  const mdatHead = new Writer().u32( mdatSize ).str( 'mdat' ).done();
  return concat( [ ftyp, meta, mdatHead, ...payloads ] );
}
